import delve from 'dlv';
import kyWithSentry from './helper/kyWithSentry';
import { EntityTypes } from 'context/entityContext';
import {
  apiTypeToEntityType,
  entityTypeToApiType,
} from 'helpers/mapEntityTypes';
import { IIterator } from 'types/shared';

interface IShortInfo {
  displayName: string;
  id: string;
  type: string;
}

interface IRecentEntityItem {
  displayName: string;
  id: string;
  is_group: boolean;
  type: EntityTypes;
  group_child_count?: number;
  primaryArtist?: IShortInfo;
  releaseDate?: number;
  album_count?: number;
  artist_count?: number;
  track_count?: number;
  grouped_album_count?: number;
  grouped_track_count?: number;
  thumbnailUrl?: string;
  parent_group_id?: string;
  data: any;
  isNotFollowed?: boolean;
}

interface IRecentEntity {
  entity: IRecentEntityItem;
  metadata?: {
    associatedAlbumsExcerpt?: IShortInfo[];
    associatedLabelsExcerpt?: IShortInfo[];
    albumsExcerpt?: IShortInfo[];
    tracksExcerpt?: IShortInfo[];
    associated_album_count?: number;
    associated_grouped_album_count?: number;
    associated_label_count?: number;
  };
}

export interface IRecentEntityRaw extends IIterator {
  type: EntityTypes;
  id: string;
}

const entityTypes = ['track', 'release', 'artist', 'label', 'project'];

const generateFetches = (types: IIterator, ids: IIterator): IIterator => {
  return types.reduce((fetches: Record<string, unknown>, type: EntityTypes) => {
    if (ids[type]) {
      const fetchItem = kyWithSentry
        .post(`/api/${entityTypeToApiType(type)}/byIds`, {
          json: ids[type],
        } as IIterator)
        .json();
      return { ...fetches, [`${type}`]: fetchItem };
    }
    return fetches;
  }, {});
};

const resolveFetches = async (
  allFetches: IIterator
): Promise<IRecentEntity[]> => {
  const currentEntities: IRecentEntity[] = [];
  for (const type in allFetches) {
    const entities = await allFetches[type];

    const actualEntities = entities.filter(
      (item: IRecentEntity): false | IRecentEntityItem =>
        delve(item, 'code') !== 404 && item.entity
    );
    currentEntities.push(...actualEntities);
  }
  return currentEntities;
};

const filterEntities = (entities: IRecentEntityRaw): IIterator => {
  const ids: IIterator = {};
  entities.forEach(
    (item: { entityType: EntityTypes; entityId: string }): void => {
      if (!ids[item.entityType]) {
        ids[item.entityType] = [];
      }

      ids[item.entityType].push(item.entityId);
    }
  );
  return ids;
};

const recentListMetadataInterceptor = async (
  input: RequestInfo /* eslint-disable-line */,
  init: RequestInit | undefined /* eslint-disable-line */,
  interceptionData?: any
): Promise<Response> => {
  try {
    const { recentList } = interceptionData;
    const idsPerEntityType = filterEntities(
      recentList.map((rl: any) => ({
        ...rl,
        entityType: apiTypeToEntityType(rl.entityType),
      }))
    );
    const fetches = generateFetches(entityTypes, idsPerEntityType);
    const currentEntities = await resolveFetches(fetches);

    return new Response(
      JSON.stringify({
        recentData: currentEntities.map((entity) => entity.entity),
        recentIsNotLoading: recentList.length
          ? Object.keys(fetches).length
          : true,
        recentError: null,
      })
    );
  } catch (error) {
    console.error(
      'An error occured during fetching recent entities data',
      new Error(error as any)
    );
    return new Response(
      JSON.stringify({
        recentData: null,
        recentIsLoading: null,
        recentError: new Error(error as any),
      })
    );
  }
};

export default recentListMetadataInterceptor;
